import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminStatsResponse, DecisionAudit } from '@kkp-fraud/shared';
import { api } from '../api';
import { useShell } from '../components/AppLayout';
import { ActionChip, DecisionChip } from '../components/Chips';
import { PageHead, Tip } from '../components/PageHead';
import { Sparkline } from '../components/Sparkline';
import { StatsTiles } from '../components/StatsTiles';
import { hitRows, needsAttention, neverHit, rulePath, ruleActions, trendOf, type TrendUnit } from '../dashboardData';
import { dateTime, ms, num, thaiDay } from '../format';
import { When } from '../components/When';
import { LoadError } from '../components/LoadError';
import { CardLink } from '../components/Links';
import { NOT_A_RULE, ruleLink } from '../links';
import { usePrimarySetId } from '../hooks/usePrimarySetId';
import { t } from '../i18n';

/**
 * Dashboard — the business view: what the engine decided, which rules did it, what needs a
 * look, and whether latency is drifting.
 *
 * Every number here is measured: `GET /admin/stats` for the window totals and hit counts,
 * `GET /admin/decisions` (the last {@link TREND_LIMIT}) for the attention list and the
 * trend. Nothing is filled in with sample data — a plausible-looking chart nobody measured
 * is the one thing on this screen that could be quoted in a meeting as fact.
 */

/** How many audited decisions the trend and the attention list read. */
export const TREND_LIMIT = 500;
const ATTENTION_MAX = 10;

const UNIT_KEY: Record<TrendUnit, string> = {
  minute: 'dash.trend.unit.minute',
  hour: 'dash.trend.unit.hour',
};

/** Today's decisions (Thai calendar day) show the clock only; anything older carries its date. */
const isToday = (iso: string) => thaiDay(iso) === thaiDay(new Date());

export function DashboardScreen() {
  const { state, settings } = useShell();
  const primaryId = usePrimarySetId();
  /**
   * Each read is `null` until it answers, and again when it fails (QA-M-03): a panel whose
   * data could not be read says so, instead of drawing zeros and "nothing yet" that nobody
   * measured — or the last good figures as if they were current.
   */
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [decisions, setDecisions] = useState<DecisionAudit[] | null>(null);
  /** The technical detail of each failed read; the sentence is `t()` at render time. */
  const [statsError, setStatsError] = useState<string | null>(null);
  const [decisionsError, setDecisionsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [s, d] = await Promise.allSettled([api.stats(settings), api.decisions(settings, TREND_LIMIT)]);
    setStats(s.status === 'fulfilled' ? s.value : null);
    setStatsError(s.status === 'rejected' ? detailOf(s.reason) : null);
    setDecisions(d.status === 'fulfilled' ? d.value : null);
    setDecisionsError(d.status === 'rejected' ? detailOf(d.reason) : null);
    setLoading(false);
  }, [settings.baseUrl]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const decided = stats?.byDecision;
  const total = stats?.total ?? 0;
  const friction =
    total > 0 ? (((decided?.STEP_UP ?? 0) + (decided?.DECLINE ?? 0)) / total) * 100 : null;

  const hits = useMemo(() => hitRows(stats?.byRule, state.draft), [stats, state.draft]);
  const hitTotal = hits.reduce((acc, r) => acc + r.hits, 0);
  /** Bars are scaled to the busiest rule, like the decision mix is to the total; the % column carries the share. */
  const topHits = hits[0]?.hits || 1;
  const idle = useMemo(() => (stats ? neverHit(stats.byRule, state.draft) : []), [stats, state.draft]);
  const attention = useMemo(() => needsAttention(decisions ?? [], ATTENTION_MAX), [decisions]);
  const trend = useMemo(() => trendOf(decisions ?? []), [decisions]);
  const unit = t(UNIT_KEY[trend.unit]);

  return (
    <>
      <PageHead title={t('screen.dash.title')} lead={t('screen.dash.console.lead')} />

      <section className="card">
        <header className="card-head">
          <h2>{t('tab.decisions.label')}</h2>
          {stats && (
            <span className="small muted">{t('dash.decisions.window', { n: total, since: dateTime(stats.since) })}</span>
          )}
          <span className="spacer" />
          {loading && (
            <span className="small muted" role="status">
              {t('btn.loading.label')}
            </span>
          )}
          <button type="button" className="btn" onClick={() => void refresh()}>
            {t('btn.refresh.label')}
          </button>
        </header>
        <div className="card-body stack">
          {statsError && <LoadError message={t('dash.error.stats')} detail={statsError} testId="dash-stats-error" />}
          {stats ? (
            <StatsTiles stats={stats} />
          ) : (
            !statsError && (
              <div className="empty small" role="status" data-testid="dash-stats-loading">
                {t('btn.loading.label')}
              </div>
            )
          )}
          {friction !== null && (
            /* The share of traffic that felt friction, in words, with the codes it adds up (QA-M-13). */
            <p className="note" style={{ margin: 0 }} data-testid="dash-friction">
              <b>{t('dash.friction.line', { pct: `${friction.toFixed(1)}%` })}</b>
            </p>
          )}
        </div>
      </section>

      <section className="card" data-testid="dash-hits">
        <header className="card-head">
          <h2>{t('dash.byRule.title')}</h2>
          {stats && <Tip>{t('dash.byRule.tip', { since: dateTime(stats.since) })}</Tip>}
          {hitTotal > 0 && (
            <span className="chip">{t(hitTotal === 1 ? 'dash.byRule.total.one' : 'dash.byRule.total.other', { n: num(hitTotal) })}</span>
          )}
        </header>
        {!stats ? (
          <div className="card-body">
            {statsError ? (
              <LoadError message={t('dash.error.hits')} detail={statsError} testId="dash-hits-error" />
            ) : (
              <div className="empty small" role="status">
                {t('btn.loading.label')}
              </div>
            )}
          </div>
        ) : hits.length === 0 ? (
          <div className="card-body">
            <div className="empty small">{t('stats.topRules.empty')}</div>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>{t('col.rule.label')}</th>
                  <th>{t('col.name.label')}</th>
                  <th style={{ width: 170 }}>{t('col.action.label')}</th>
                  <th style={{ width: '22%' }} aria-hidden="true" />
                  <th className="num-cell" style={{ width: 80 }}>
                    {t('col.hits.label')}
                  </th>
                  <th className="num-cell" style={{ width: 70 }}>
                    {t('dash.col.share')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {hits.map((row) => {
                  const path = rulePath(row.id, primaryId);
                  return (
                    <tr key={row.id} data-testid={`hit-row-${row.id}`} className={row.rule && !row.rule.enabled ? 'disabled' : undefined}>
                      <td className="mono nowrap">{path ? <Link to={path}>{row.id}</Link> : row.id}</td>
                      <td className="small muted">{row.rule?.name ?? '—'}</td>
                      <td>
                        <div className="row-wrap">
                          {row.rule ? ruleActions(row.rule).map((a, i) => <ActionChip key={i} action={a} />) : '—'}
                        </div>
                      </td>
                      <td>
                        <div className="bar">
                          <i style={{ width: `${Math.max(2, (row.hits / topHits) * 100).toFixed(1)}%` }} />
                        </div>
                      </td>
                      <td className="num-cell">{num(row.hits)}</td>
                      <td className="num-cell small">{row.share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {idle.length > 0 && (
          <div className="row-wrap neverhit" data-testid="never-hit">
            <span className="small muted">{t('dash.neverHit.label')}</span>
            <Tip>{t('dash.neverHit.tip')}</Tip>
            {idle.map((r) => {
              const path = rulePath(r.id, primaryId);
              return path ? (
                <Link key={r.id} to={path} className="chip mono">
                  {r.id}
                </Link>
              ) : (
                <span key={r.id} className="chip mono">
                  {r.id}
                </span>
              );
            })}
          </div>
        )}
      </section>

      <section className="card" data-testid="dash-attention">
        <header className="card-head">
          <h2>{t('dash.attention.title')}</h2>
          <Tip>{t('dash.attention.tip')}</Tip>
          <span className="spacer" />
          <Link to="/log" className="small">
            {t('dash.attention.log.link')}
          </Link>
        </header>
        {!decisions ? (
          <div className="card-body">
            {decisionsError ? (
              <LoadError message={t('dash.error.attention')} detail={decisionsError} testId="dash-attention-error" />
            ) : (
              <div className="empty small" role="status">
                {t('btn.loading.label')}
              </div>
            )}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>{t('col.time.label')}</th>
                  <th style={{ width: 96 }}>{t('log.col.card')}</th>
                  <th style={{ width: 150 }}>{t('col.decision.label')}</th>
                  {/* The one column that takes the spare width: who was paid, by name and id. */}
                  <th>{t('cases.col.merchant')}</th>
                  <th className="num-cell" style={{ width: 120 }}>
                    {t('decisions.col.amount')}
                  </th>
                  <th style={{ width: 140 }}>{t('field.network.label')}</th>
                  <th className="num-cell" style={{ width: 64 }}>
                    MCC
                  </th>
                  <th style={{ width: 200 }}>{t('dash.col.fired')}</th>
                  <th className="num-cell" style={{ width: 110 }}>
                    {t('dash.col.engine')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {attention.length === 0 && (
                  <tr>
                    <td colSpan={9} className="empty small">
                      {t('dash.attention.empty')}
                    </td>
                  </tr>
                )}
                {attention.map((r) => (
                  <tr key={r.id} data-testid={`attention-${r.transactionId}`}>
                    <td className="mono small nowrap">
                      <When iso={r.eventTimeUtc} clock={isToday(r.eventTimeUtc)} />
                    </td>
                    {/* UX review P1-3: the card opens its decisions and cases in Investigate. */}
                    <td className="mono small nowrap">
                      <CardLink cardId={r.cardId} testid={`dash-card-${r.transactionId}`} />
                    </td>
                    <td>
                      <div className="row-wrap">
                        <DecisionChip decision={r.decision} />
                        {r.degraded && (
                          <span className="chip warn" title={t('decisions.degraded.tip')}>
                            {t('flag.degraded.chip')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="small" data-testid={`dash-merchant-${r.transactionId}`}>
                      {r.txn?.merchant_name || r.txn?.merchant_id ? (
                        <>
                          <div>{r.txn?.merchant_name ?? '—'}</div>
                          {r.txn?.merchant_id && <div className="sub2 mono">{r.txn.merchant_id}</div>}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num-cell">{num(r.amountThb, 2)}</td>
                    <td className="small">
                      <div>{r.network}</div>
                      {r.txn && (
                        <div className="sub2">
                          {r.txn.ecommerce ? t('dash.channel.online') : r.txn.card_present ? t('dash.channel.present') : ''}
                        </div>
                      )}
                    </td>
                    <td className="num-cell small">{r.mcc ?? '—'}</td>
                    <td>
                      <div className="row-wrap">
                        {r.reasons.length > 0
                          ? r.reasons.map((id) =>
                              NOT_A_RULE.has(id) ? (
                                <span key={id} className="chip mono small">
                                  {id}
                                </span>
                              ) : (
                                <Link key={id} to={ruleLink(id, undefined, primaryId)} className="chip mono small" title={t('links.rule.title', { id })}>
                                  {id}
                                </Link>
                              ),
                            )
                          : '—'}
                      </div>
                    </td>
                    <td className="num-cell small">{ms(r.engineLatencyMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <TrendSection decisions={decisions?.length ?? null} error={decisionsError} trend={trend} unit={unit} />
    </>
  );
}

/** A failed read's technical detail — what the API client said, for the secondary line. */
function detailOf(reason: unknown): string {
  return (reason as Error)?.message ?? String(reason);
}

function TrendSection({
  decisions,
  error,
  trend,
  unit,
}: {
  /** How many decisions the trend read; `null` while they are loading or when the read failed. */
  decisions: number | null;
  error: string | null;
  trend: ReturnType<typeof trendOf>;
  unit: string;
}) {
  const counts = trend.points.map((p) => p.count);
  const p95s = trend.points.map((p) => p.p95);
  const empty = trend.points.length === 0;
  return (
    <section className="card" data-testid="dash-trend">
      <header className="card-head">
        <h2>{t('dash.trend.title')}</h2>
        <Tip>{t('dash.trend.tip', { n: num(decisions ?? TREND_LIMIT), unit })}</Tip>
        {!empty && <span className="chip">{t('dash.trend.per.chip', { unit })}</span>}
        {!empty && <span className="chip">{t('decisions.limit.option', { n: num(decisions) })}</span>}
      </header>
      <div className="card-body">
        {decisions === null ? (
          error ? (
            <LoadError message={t('dash.error.trend')} detail={error} testId="dash-trend-error" />
          ) : (
            <div className="empty small" role="status">
              {t('btn.loading.label')}
            </div>
          )
        ) : (
          empty && <div className="empty small">{t('dash.trend.empty')}</div>
        )}
        {decisions !== null && (
          <div className="grid g2">
            <TrendFigure
              label={t('dash.trend.volume.label')}
              values={counts}
              format={(n) => num(n)}
              testId="spark-volume"
            />
            <TrendFigure label={t('dash.trend.latency.label')} values={p95s} format={ms} testId="spark-latency" />
          </div>
        )}
      </div>
    </section>
  );
}

function TrendFigure({
  label,
  values,
  format,
  testId,
}: {
  label: string;
  values: (number | null)[];
  format: (n: number) => string;
  testId: string;
}) {
  const finite = values.filter((v): v is number => v !== null && Number.isFinite(v));
  const current = [...values].reverse().find((v): v is number => v !== null && Number.isFinite(v));
  return (
    <div className="trendfig">
      <div className="trendhead">
        <span className="lbl">{label}</span>
        <span className="val">{current === undefined ? '—' : format(current)}</span>
        {current !== undefined && <span className="small muted">{t('col.now.label')}</span>}
      </div>
      <Sparkline values={values} label={label} testId={testId} />
      {finite.length > 0 && (
        <div className="small muted mono">
          {t('dash.trend.range', { min: format(Math.min(...finite)), max: format(Math.max(...finite)) })}
        </div>
      )}
    </div>
  );
}
